import { caravanTraderSoldGoodIds } from '@helpers/caravan/caravan-trader-goods';
import { getEntry } from '@helpers/content/content';
import {
  isRecipeCraftable,
  isRecipeTownUnique,
  recipesProducing,
} from '@helpers/crafting/recipes';
import { getMaterialQuantity } from '@helpers/item/materials';
import { armoryGet } from '@helpers/kingdom/armory';
import { rngNumberRange } from '@helpers/rng';
import { townReputationTierValueResolve } from '@helpers/town/reputation/town-reputation-tier-value';
import { allGatherableMaterialIds } from '@helpers/world-node/world-node-gathering';
import type {
  CommissionOfferContent,
  CommissionOfferRequirement,
  CommissionOfferSlot,
  CommissionRequirement,
  CommissionRequirementEntry,
  EligibleCommissionOffer,
  EquipmentContent,
  GameState,
  ItemContent,
  MonsterContent,
  TownId,
} from '@interfaces';

// Only an undiscovered drop-gated recipe blocks, never tradeskill level; gatherable, trader-sold and town-shop goods need no recipe.
function commissionOfferRequirementCraftable(
  requirement: CommissionOfferRequirement,
  obtainable: Set<string>,
): boolean {
  if ('monsterId' in requirement) return true;

  const resultId =
    'equipmentId' in requirement ? requirement.equipmentId : requirement.itemId;
  if (obtainable.has(resultId)) return true;

  const recipes = recipesProducing(resultId);
  return (
    recipes.length === 0 ||
    recipes.some(
      (recipe) => isRecipeTownUnique(recipe.id) || isRecipeCraftable(recipe.id),
    )
  );
}

// Shared by caravan and town commission generation - both draw from the same weighted CommissionOfferSlot[] pool shape.
export function eligibleCommissionOffers(
  slots: CommissionOfferSlot[],
): EligibleCommissionOffer[] {
  if (slots.length === 0) return [];

  const obtainable = new Set<string>([
    ...allGatherableMaterialIds(),
    ...caravanTraderSoldGoodIds(),
  ]);

  return slots
    .map((slot) => {
      const offer = getEntry<CommissionOfferContent>(slot.commissionOfferId);
      const craftable = offer?.requirements.every((requirement) =>
        commissionOfferRequirementCraftable(requirement, obtainable),
      );
      return offer && craftable ? { offer, weight: slot.weight } : undefined;
    })
    .filter((slot): slot is EligibleCommissionOffer => !!slot);
}

function commissionOfferTierMultiplier(
  offer: CommissionOfferContent,
  townId?: TownId,
  state?: GameState,
): number {
  if (!townId || offer.reputationTierMultipliers.length === 0) return 1;

  // Falls back to 1, not the resolver's own 0-if-unmatched, so an offer that forgets to author tier 0 doesn't become free.
  return (
    townReputationTierValueResolve(
      townId,
      offer.reputationTierMultipliers,
      state,
    ) || 1
  );
}

export function commissionOfferReputationReward(
  offer: CommissionOfferContent,
  townId?: TownId,
  state?: GameState,
): number {
  return Math.round(
    offer.townReputationReward *
      commissionOfferTierMultiplier(offer, townId, state),
  );
}

export function rollCommissionRequirements(
  offer: CommissionOfferContent,
  townId?: TownId,
  state?: GameState,
): CommissionRequirement[] {
  const multiplier = commissionOfferTierMultiplier(offer, townId, state);

  return offer.requirements.map((requirement) => {
    const quantity = Math.round(
      rngNumberRange(requirement.quantityMin, requirement.quantityMax) *
        multiplier,
    );

    if ('equipmentId' in requirement) {
      return { equipmentId: requirement.equipmentId, quantity };
    }
    if ('monsterId' in requirement) {
      return { monsterId: requirement.monsterId, quantity, progress: 0 };
    }
    return { itemId: requirement.itemId, quantity };
  });
}

// Reads off an explicit `state` when given, so callers can re-validate against a commit-time state.
export function commissionRequirementOwnedQuantity(
  requirement: CommissionRequirement,
  state?: GameState,
): number {
  if ('equipmentId' in requirement) {
    const armory = state ? state.armory : armoryGet();
    return armory.filter((item) => item.equipmentId === requirement.equipmentId)
      .length;
  }

  // A kill requirement's progress is tallied on the requirement itself, not read from inventory.
  if ('monsterId' in requirement) return requirement.progress;

  return state
    ? (state.materials[requirement.itemId]?.quantity ?? 0)
    : getMaterialQuantity(requirement.itemId);
}

export function commissionRequirementsSatisfied(
  requirements: CommissionRequirement[],
  state?: GameState,
): boolean {
  return requirements.every(
    (requirement) =>
      commissionRequirementOwnedQuantity(requirement, state) >=
      requirement.quantity,
  );
}

// Every UI surface reuses the same icon-row rendering the tradeskill panel already uses for recipe requirements.
export function buildCommissionRequirementEntries(
  requirements: CommissionRequirement[],
): CommissionRequirementEntry[] {
  return requirements.map((requirement) => {
    if ('equipmentId' in requirement) {
      return {
        kind: 'equipment',
        content: getEntry<EquipmentContent>(requirement.equipmentId),
        spritesheet: 'equipment',
        quantity: requirement.quantity,
        owned: commissionRequirementOwnedQuantity(requirement),
      };
    }

    if ('monsterId' in requirement) {
      return {
        kind: 'monster',
        content: getEntry<MonsterContent>(requirement.monsterId),
        spritesheet: 'monster',
        quantity: requirement.quantity,
        owned: commissionRequirementOwnedQuantity(requirement),
      };
    }

    return {
      kind: 'item',
      content: getEntry<ItemContent>(requirement.itemId),
      spritesheet: 'item',
      quantity: requirement.quantity,
      owned: commissionRequirementOwnedQuantity(requirement),
    };
  });
}
